import React from 'react';
import { motion } from 'framer-motion';
import { SparklesIcon } from 'lucide-react';
import { DjaySpecimen } from '../../../mascot';
import type { DjayAccessory, DjayTier, DjayVariant } from '../../../mascot';
import { useMotionBudget } from '../../../app/hooks/useMotionBudget';
export type CollectibleKind = 'accessory' | 'tier' | 'variant';
export interface CollectibleNodeData {
  kind: CollectibleKind;
  id: string;
  name: string;
  /** Accessory worn by the avatar (when kind === 'accessory'). */
  accessory?: DjayAccessory;
  /** Tier evolution shown (when kind === 'tier'). */
  tier?: DjayTier;
  /** Variant colour scheme shown (when kind === 'variant'). */
  variant?: DjayVariant;
}
interface CollectibleCheckpointProps {
  collectible: CollectibleNodeData;
  align: 'left' | 'right';
  /** Deep-link into the Collection (trophy room). */
  onOpenCollection: () => void;
}
const KIND_LABEL: Record<CollectibleKind, string> = {
  accessory: 'Accessoire ontgrendeld!',
  tier: 'Rang ontgrendeld!',
  variant: 'Look ontgrendeld!'
};
/**
 * Inline "you-earned-this" checkpoint on the Voortgang journey.
 *
 * Echoes the CheckpointFlag aesthetic (small badge on a little post, label
 * card to the side) but instead of an emoji it shows the actual collectible
 * the learner already owns — the avatar wearing the accessory, evolved to the
 * tier, or in the unlocked colour scheme. Tapping it deep-links to the
 * matching card in the Collection.
 */
export function CollectibleCheckpoint({
  collectible,
  align,
  onOpenCollection
}: CollectibleCheckpointProps) {
  const {
    shouldAnimate
  } = useMotionBudget();
  const alignClass = align === 'left' ? 'self-start ml-2 sm:ml-8' : 'self-end mr-2 sm:mr-8';
  const labelCard = <div className={`bg-white/95 backdrop-blur-sm border-2 border-amber-200 px-3 py-1.5 rounded-xl shadow-sm max-w-[150px] ${align === 'right' ? 'text-right' : 'text-left'}`} data-id="element-1887" data-mp="3mhaftcr9oh45">
      <span className="text-[9px] font-bold text-amber-500 uppercase tracking-wider flex items-center gap-1 leading-none mb-0.5 justify-start" data-id="element-1888" data-mp="cghlhoaxcw1">
        <SparklesIcon className="w-2.5 h-2.5 flex-shrink-0" data-id="element-1889" data-mp="36qazkarwby1o" />{' '}
        {KIND_LABEL[collectible.kind]}
      </span>
      <span className="text-xs font-black text-slate-700 leading-tight block truncate" data-id="element-1890" data-mp="3gi6zlrdjly8j">
        {collectible.name}
      </span>
    </div>;
  return <motion.button type="button" onClick={() => {
    if (collectible.kind === 'variant') {
      sessionStorage.setItem('collectionFocus', collectible.id);
    }
    onOpenCollection();
  }} initial={{
    opacity: 0,
    y: 10
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-50px'
  }} whileHover={{
    scale: 1.03
  }} whileTap={{
    scale: 0.97
  }} className={`flex items-center gap-2 relative z-10 my-3 ${alignClass}`} aria-label={`${KIND_LABEL[collectible.kind]} ${collectible.name}. Open de collectie.`} data-id="element-1891" data-mp="3bq5sskz58zpw">
      {align === 'right' && labelCard}

      <div className="relative" data-id="element-1892" data-mp="foit51uy9o8o">
        <div className="w-1 h-12 bg-amber-200 rounded-full absolute left-1/2 -translate-x-1/2 bottom-0" data-id="element-1893" data-mp="kliyfajz6myq" />
        <div className="w-14 h-14 bg-amber-50 border-2 border-amber-300 rounded-full flex items-center justify-center shadow-[0_0_14px_rgba(251,191,36,0.35)] relative z-10 overflow-hidden" data-id="element-1894" data-mp="5vbojj81ac9n">
          <div className="w-12 h-12" data-id="element-1895" data-mp="3t0t5yy6mwkdw">
            <DjaySpecimen size="lg" emotion="celebrating" look={{
            variant: collectible.variant ?? 'default',
            accessory: collectible.accessory ?? 'none',
            tier: collectible.tier ?? 'rookie'
          }} animate={shouldAnimate} className="w-full h-full" data-id="element-1896" data-mp="3szrhns0ly1e8" />
          </div>
        </div>
      </div>

      {align === 'left' && labelCard}
    </motion.button>;
}